<template>
  <div class="profile">
    <header class="section-header">
      <div>
        <h1>Perfil de usuario</h1>
        <p class="muted">Actualiza tus datos y controla la seguridad de tu cuenta.</p>
      </div>
      <button class="ghost danger" @click="handleLogout">
        Cerrar sesión
      </button>
    </header>

    <div v-if="loading" class="profile-grid skeleton-profile" role="status" aria-live="polite">
      <span class="sr-only">Cargando perfil...</span>
      <section class="card">
        <div class="skeleton skeleton-line lg" style="width: 40%"></div>
        <div class="avatar-row">
          <div class="skeleton skeleton-circle" style="width: 86px; height: 86px"></div>
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line" style="width: 120px"></div>
            <div class="skeleton skeleton-line" style="width: 180px"></div>
          </div>
        </div>
        <div class="form-grid">
          <div class="skeleton skeleton-line skeleton-field" style="width: 100%"></div>
          <div class="skeleton skeleton-line skeleton-field" style="width: 100%"></div>
          <div class="skeleton skeleton-line skeleton-field" style="width: 100%"></div>
          <div class="skeleton skeleton-line skeleton-field" style="width: 100%"></div>
          <div class="skeleton skeleton-line skeleton-field" style="width: 100%"></div>
          <div class="skeleton skeleton-line skeleton-field" style="width: 100%"></div>
        </div>
        <div class="card-actions">
          <div class="skeleton skeleton-line" style="width: 140px; height: 34px"></div>
        </div>
      </section>
      <section class="card">
        <div class="skeleton skeleton-line lg" style="width: 34%"></div>
        <div class="form-grid single">
          <div class="skeleton skeleton-line skeleton-field" style="width: 100%"></div>
          <div class="skeleton skeleton-line skeleton-field" style="width: 100%"></div>
          <div class="skeleton skeleton-line skeleton-field" style="width: 100%"></div>
        </div>
        <div class="security-actions">
          <div class="skeleton skeleton-line" style="width: 90px; height: 32px"></div>
          <div class="skeleton skeleton-line" style="width: 160px; height: 32px"></div>
        </div>
      </section>
    </div>
    <section v-if="error" class="state error">{{ error }}</section>

    <div v-if="!loading" class="profile-grid">
      <section class="card">
        <h2>Datos básicos</h2>
        <div class="avatar-row">
          <div class="avatar">
            <img v-if="avatarPreview" :src="avatarPreview" alt="Avatar" />
            <span v-else>{{ initials }}</span>
          </div>
          <div class="avatar-actions">
            <label class="ghost file-btn">
              Subir imagen
              <input type="file" accept="image/*" @change="onAvatarSelected" hidden />
            </label>
            <p class="muted small">PNG/JPG. Se sube en base64.</p>
          </div>
        </div>

        <div class="form-grid">
          <label>
            Email
            <input
              v-model="form.email"
              type="email"
              name="email"
              autocomplete="email"
              inputmode="email"
              readonly
              aria-readonly="true"
            />
          </label>
          <label>
            Nombre
            <input
              v-model="form.firstName"
              type="text"
              name="firstName"
              autocomplete="given-name"
              placeholder="Nombre"
            />
          </label>
          <label>
            Apellidos
            <input
              v-model="form.lastName"
              type="text"
              name="lastName"
              autocomplete="family-name"
              placeholder="Apellidos"
            />
          </label>
          <label>
            Teléfono
            <input
              v-model="form.phone"
              type="text"
              name="phone"
              autocomplete="tel"
              placeholder="+34 600 000 000"
            />
          </label>
          <label>
            País
            <input
              v-model="form.country"
              type="text"
              name="country"
              autocomplete="country-name"
              placeholder="España"
            />
          </label>
          <label>
            Ciudad
            <input
              v-model="form.city"
              type="text"
              name="city"
              autocomplete="address-level2"
              placeholder="Madrid"
            />
          </label>
          <label>
            Empresa
            <input
              v-model="form.company"
              type="text"
              name="company"
              autocomplete="organization"
              placeholder="Tu empresa"
            />
          </label>
          <label>
            DNI
            <input
              v-model="form.dni"
              type="text"
              name="dni"
              autocomplete="off"
              placeholder="12345678X"
            />
          </label>
        </div>
        <div class="card-actions">
          <button class="primary" @click="saveProfile" :disabled="saving">
            {{ saving ? "Guardando..." : "Guardar cambios" }}
          </button>
        </div>
      </section>

      <section class="card">
        <h2>Seguridad</h2>
        <div class="form-grid single">
          <label>
            Contraseña actual
            <input v-model="password.current" type="password" placeholder="••••••••" />
          </label>
          <label>
            Nueva contraseña
            <input v-model="password.next" type="password" placeholder="Mínimo 6 caracteres" />
          </label>
          <label>
            Repetir nueva contraseña
            <input v-model="password.confirm" type="password" placeholder="Repite la contraseña" />
          </label>
        </div>
        <div class="security-actions">
          <button class="ghost" @click="clearPassword">Limpiar</button>
          <button class="primary" @click="savePassword" :disabled="savingPassword">
            {{ savingPassword ? "Actualizando..." : "Cambiar contraseña" }}
          </button>
        </div>
        <p v-if="passwordError" class="form-error">{{ passwordError }}</p>
        <p v-if="passwordSuccess" class="form-success">{{ passwordSuccess }}</p>
      </section>
    </div>
  </div>
</template>

<script>
import { fetchMe, updateMe, changePassword } from "../services/profile";
import { logout } from "../services/auth";

export default {
  name: "ProfileView",
  data() {
    return {
      loading: false,
      saving: false,
      savingPassword: false,
      error: "",
      passwordError: "",
      passwordSuccess: "",
      form: {
        email: "",
        firstName: "",
        lastName: "",
        phone: "",
        country: "",
        city: "",
        company: "",
        dni: "",
        profileImg: null,
      },
      avatarPreview: "",
      password: {
        current: "",
        next: "",
        confirm: "",
      },
    };
  },
  async mounted() {
    await this.loadProfile();
  },
  computed: {
    initials() {
      const first = this.form.firstName?.trim()?.[0] || "";
      const last = this.form.lastName?.trim()?.[0] || "";
      const fallback = this.form.email?.trim()?.[0] || "U";
      return `${first}${last}`.toUpperCase() || fallback.toUpperCase();
    },
  },
  methods: {
    async loadProfile() {
      this.loading = true;
      this.error = "";
      try {
        const me = await fetchMe();
        const profileImg = me?.profileImg ?? me?.profile_img ?? null;
        const profileImgData =
          typeof profileImg === "string" ? profileImg : profileImg?.data || "";
        this.form = {
          email: me?.email || "",
          firstName: me?.firstName ?? me?.first_name ?? "",
          lastName: me?.lastName ?? me?.last_name ?? "",
          phone: me?.phone || "",
          country: me?.country || "",
          city: me?.city || "",
          company: me?.company || "",
          dni: me?.dni || "",
          profileImg: null,
        };
        this.avatarPreview = profileImgData;
      } catch (err) {
        this.error = err?.message || "No se pudo cargar el perfil.";
      } finally {
        this.loading = false;
      }
    },
    async onAvatarSelected(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      this.avatarPreview = URL.createObjectURL(file);
      const data = await this.fileToBase64(file);
      this.form.profileImg = {
        name: file.name,
        type: "IMAGE",
        format: "BASE64",
        data,
      };
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async saveProfile() {
      this.error = "";
      this.saving = true;
      try {
        const normalize = (value) => {
          if (value == null) return null;
          const trimmed = String(value).trim();
          return trimmed === "" ? null : trimmed;
        };
        const payload = {
          firstName: normalize(this.form.firstName),
          lastName: normalize(this.form.lastName),
          phone: normalize(this.form.phone),
          country: normalize(this.form.country),
          city: normalize(this.form.city),
          company: normalize(this.form.company),
          dni: normalize(this.form.dni),
          profileImg: this.form.profileImg,
        };
        await updateMe(payload);
        await this.loadProfile();
      } catch (err) {
        this.error = err?.message || "No se pudo guardar el perfil.";
      } finally {
        this.saving = false;
      }
    },
    clearPassword() {
      this.password = { current: "", next: "", confirm: "" };
      this.passwordError = "";
      this.passwordSuccess = "";
    },
    async savePassword() {
      this.passwordError = "";
      this.passwordSuccess = "";
      if (!this.password.current || !this.password.next) {
        this.passwordError = "Completa la contraseña actual y la nueva.";
        return;
      }
      if (this.password.next !== this.password.confirm) {
        this.passwordError = "Las nuevas contraseñas no coinciden.";
        return;
      }
      this.savingPassword = true;
      try {
        await changePassword({
          currentPassword: this.password.current,
          newPassword: this.password.next,
        });
        this.passwordSuccess = "Contraseña actualizada.";
        this.clearPassword();
      } catch (err) {
        const message = err?.message || "No se pudo actualizar la contraseña.";
        this.passwordError =
          message === "Credenciales incorrectas."
            ? "La contraseña actual no es correcta."
            : message;
      } finally {
        this.savingPassword = false;
      }
    },
    async handleLogout() {
      await logout();
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
.profile {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.section-header h1 {
  margin: 0 0 6px;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
}

.card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 24px;
  box-shadow: var(--shadow);
  display: grid;
  gap: 18px;
}

.avatar-row {
  display: flex;
  gap: 16px;
  align-items: center;
}

.avatar {
  width: 86px;
  height: 86px;
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(34, 197, 94, 0.2));
  border: 1px solid rgba(56, 189, 248, 0.35);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 24px;
  color: var(--text);
  overflow: hidden;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-actions {
  display: grid;
  gap: 6px;
}

.file-btn {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.form-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.form-grid.single {
  grid-template-columns: 1fr;
}

.form-grid label {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 13px;
}

.form-grid input {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 10px;
  color: var(--text);
}

.card-actions {
  display: flex;
  justify-content: flex-end;
}

.security-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.skeleton-field {
  height: 38px;
}

.card-actions button,
.security-actions button {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.primary {
  background: linear-gradient(120deg, var(--accent), #22c55e);
  color: #071022;
  border: none;
  padding: 10px 16px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
}

.ghost {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text);
  padding: 10px 16px;
  border-radius: 999px;
  cursor: pointer;
}

.ghost.danger {
  color: var(--danger);
  border-color: rgba(239, 68, 68, 0.4);
}

.muted {
  color: var(--muted);
}

.small {
  font-size: 12px;
}

.state {
  padding: 24px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.form-error {
  color: var(--danger);
  margin: 0;
  font-size: 13px;
}

.form-success {
  color: var(--accent-3);
  margin: 0;
  font-size: 13px;
}

@media (max-width: 700px) {
  .avatar-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .card-actions {
    justify-content: stretch;
  }

  .card-actions .primary {
    width: 100%;
  }

  .security-actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
